import { useRef, useState, type ReactNode } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Button, Empty, Modal, Notice, Table, type ColumnDef } from '../design/index.js'
import type { SheetImportPreview, SheetImportResult, SheetImportRow } from '../app/api.js'
import { messageFor } from './feedback.js'

/**
 * Download a sheet, fill it in, upload it, see what it would do, apply it —
 * for imports straight onto one project (the schedule, the team).
 *
 * Nothing is written until "Apply". Apply sends the same file again and the
 * server re-checks it, so the counts on the button are what was previewed but
 * the rows written are checked against the project as it is at that moment.
 */
export interface SheetImportDialogProps {
  readonly title: string
  /** What the sheet holds and how rows are matched, in a sentence or two. */
  readonly intro: ReactNode
  readonly templateUrl: string
  readonly validate: (base64: string, fileName: string) => Promise<SheetImportPreview>
  readonly apply: (base64: string, fileName: string) => Promise<SheetImportResult>
  readonly onApplied: (result: SheetImportResult) => void
  readonly onClose: () => void
}

const MAX_MB = 25

const ACTION_WORDS: Readonly<Record<SheetImportRow['action'], string>> = {
  CREATE: 'New',
  UPDATE: 'Changes',
  UNCHANGED: 'No change',
  REJECT: 'Cannot be imported',
}

/** Reads a picked file as base64, without the data: prefix. */
export const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('That file could not be read.'))
    reader.onload = () => {
      const result = String(reader.result ?? '')
      resolve(result.slice(result.indexOf(',') + 1))
    }
    reader.readAsDataURL(file)
  })

export const SheetImportDialog = ({
  title, intro, templateUrl, validate, apply, onApplied, onClose,
}: SheetImportDialogProps) => {
  const picker = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<{ name: string; base64: string } | null>(null)
  const [preview, setPreview] = useState<SheetImportPreview | null>(null)
  const [done, setDone] = useState<SheetImportResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const check = useMutation({
    mutationFn: async (picked: File) => {
      if (picked.size > MAX_MB * 1024 * 1024) throw new Error(`The file is larger than ${MAX_MB} MB.`)
      const base64 = await readFileAsBase64(picked)
      const answer = await validate(base64, picked.name)
      return { answer, name: picked.name, base64 }
    },
    onSuccess: ({ answer, name, base64 }) => {
      setError(null); setDone(null); setFile({ name, base64 }); setPreview(answer)
    },
    onError: (err) => { setPreview(null); setFile(null); setError(messageFor(err)) },
  })

  const commit = useMutation({
    mutationFn: () => {
      if (!file) throw new Error('Choose a file first.')
      return apply(file.base64, file.name)
    },
    onSuccess: (result) => { setError(null); setDone(result); setPreview(result); onApplied(result) },
    onError: (err) => setError(messageFor(err)),
  })

  const busy = check.isPending || commit.isPending
  const counts = preview?.counts
  const writing = counts ? counts.create + counts.update : 0
  // The rows worth reading: what changes and what was refused. "No change"
  // rows are counted, not listed.
  const rows = (preview?.rows ?? []).filter((r) => r.action !== 'UNCHANGED')

  const columns: ReadonlyArray<ColumnDef<SheetImportRow>> = [
    { key: 'sheetRow', header: 'Row', width: 70, align: 'right' },
    { key: 'action', header: 'What happens', width: 170, render: (r) => (
      <span className={r.action === 'REJECT' ? 'over' : undefined}>{ACTION_WORDS[r.action]}</span>
    ) },
    { key: 'label', header: 'What', width: 240 },
    {
      key: 'changes',
      header: 'Details',
      render: (r) => (r.problem
        ? r.problem
        : r.changes.length > 0
          ? r.changes.map((c) => `${c.field}: ${c.from} → ${c.to}`).join(' · ')
          : '—'),
    },
  ]

  return (
    <Modal
      title={title}
      wide
      onClose={onClose}
      actions={
        <>
          <Button kind="ghost" onClick={onClose}>{done ? 'Close' : 'Cancel'}</Button>
          {!done ? (
            <Button kind="primary" disabled={busy || !preview || writing === 0} onClick={() => commit.mutate()}>
              {commit.isPending ? 'Applying…' : `Apply ${writing} row${writing === 1 ? '' : 's'}`}
            </Button>
          ) : null}
        </>
      }
    >
      <p className="helper">{intro}</p>
      <div className="row row--wrap" style={{ marginBottom: 16 }}>
        <Button kind="ghost" disabled={busy} onClick={() => { window.location.href = templateUrl }}>
          Download template
        </Button>
        <input
          ref={picker}
          type="file"
          accept=".xlsx"
          aria-label="Spreadsheet to import"
          hidden
          onChange={(event) => {
            const picked = event.currentTarget.files?.[0]
            event.currentTarget.value = ''
            if (picked) check.mutate(picked)
          }}
        />
        <Button kind="primary" disabled={busy} onClick={() => picker.current?.click()}>
          {check.isPending ? 'Reading…' : preview ? 'Choose another file' : 'Choose file and check'}
        </Button>
      </div>

      {error ? <Notice kind="bad">{error}</Notice> : null}

      {done ? (
        <Notice kind="ok">
          Done. {done.created} added, {done.updated} changed
          {done.counts.reject > 0 ? `, ${done.counts.reject} skipped because they could not be imported` : ''}.
        </Notice>
      ) : null}

      {preview && counts ? (
        <>
          <p className="helper">
            {preview.fileName ? `${preview.fileName} · ` : ''}Read as: {preview.layout} ·{' '}
            {counts.create} new · {counts.update} changed · {counts.unchanged} unchanged ·{' '}
            {counts.reject} cannot be imported.
            {counts.reject > 0 ? ' Rows that cannot be imported are skipped, not guessed at.' : ''}
          </p>
          {preview.ignoredHeaders.length > 0 ? (
            <p className="helper">Columns not read: {preview.ignoredHeaders.join(', ')}.</p>
          ) : null}
          <Table
            columns={columns}
            rows={rows}
            getKey={(r) => String(r.sheetRow)}
            empty={<Empty title="Nothing to change" body={['Every row matches what is already here.']} />}
          />
        </>
      ) : null}
    </Modal>
  )
}
